import Vue from 'vue';
import { VuexBuilder } from '@helpers/vuexBuilder';

const state = {
	formsList: [],
	formsById: {},
	assignedFormsByEmployeeId: {},
	submissionsById: {},
	operations: {
		list: false,
		listAssignedToEmployee: false,
		create: false,
		appendVersion: false,
		getLatest: false,
		getByVersion: false,
		createSubmission: false
	}
};

const getters = {
	formsList: state => state.formsList,
	formById: state => id => state.formsById[id] || null,
	assignedFormsForEmployee: state => employeeId => state.assignedFormsByEmployeeId[employeeId] || [],
	submissionById: state => id => state.submissionsById[id] || null,
	isListing: state => state.operations.list,
	isListingAssignedToEmployee: state => state.operations.listAssignedToEmployee,
	isCreating: state => state.operations.create,
	isAppendingVersion: state => state.operations.appendVersion,
	isGettingLatest: state => state.operations.getLatest,
	isGettingByVersion: state => state.operations.getByVersion,
	isCreatingSubmission: state => state.operations.createSubmission
};

const actions = {
	async list({ commit, rootState }) {
		return await new VuexBuilder(commit)
			.operationKey('list')
			.request(() => Vue.axios.get(`/api/${rootState.tenantId}/forms`))
			.commitData('SET_LIST')
			.execute();
	},
	async listAssignedToEmployee({ commit, rootState }, employeeId) {
		commit('START_OPERATION', 'listAssignedToEmployee');

		try {
			const response = await Vue.axios.get(`/api/${rootState.tenantId}/employees/${employeeId}/profile/forms`);
			commit('SET_ASSIGNED_FORMS_FOR_EMPLOYEE', {
				employeeId,
				forms: response.data
			});
			return response.data;
		} finally {
			commit('FINISH_OPERATION', 'listAssignedToEmployee');
		}
	},
	async create({ commit, rootState }, payload) {
		return await new VuexBuilder(commit)
			.operationKey('create')
			.request(() => Vue.axios.post(`/api/${rootState.tenantId}/forms`, payload))
			.commitData('UPSERT_FORM')
			.successToast('Form created successfully.')
			.errorToast('Failed to create form.')
			.execute();
	},
	async appendVersion({ commit, rootState }, { formId, definition }) {
		return await new VuexBuilder(commit)
			.operationKey('appendVersion')
			.request(() => Vue.axios.post(`/api/${rootState.tenantId}/forms/${formId}/versions`, { definition }))
			.commitData('UPSERT_FORM')
			.successToast('Form version created successfully.')
			.errorToast('Failed to create form version.')
			.execute();
	},
	async getLatest({ commit, rootState }, formId) {
		return await new VuexBuilder(commit)
			.operationKey('getLatest')
			.request(() => Vue.axios.get(`/api/${rootState.tenantId}/forms/${formId}`))
			.commitData('UPSERT_FORM')
			.execute();
	},
	async getByVersion({ commit, rootState }, { formId, versionNo }) {
		return await new VuexBuilder(commit)
			.operationKey('getByVersion')
			.request(() => Vue.axios.get(`/api/${rootState.tenantId}/forms/${formId}/versions/${versionNo}`))
			.commitData('UPSERT_FORM')
			.execute();
	},
	async createSubmission({ commit, rootState }, { formId, values, parties, successMessage = 'Form submission created successfully.', errorMessage = 'Failed to create form submission.' }) {
		return await new VuexBuilder(commit)
			.operationKey('createSubmission')
			.request(() => Vue.axios.post(`/api/${rootState.tenantId}/forms/${formId}/submissions`, { values, parties }))
			.commitData('UPSERT_SUBMISSION')
			.successToast(successMessage)
			.errorToast(errorMessage)
			.execute();
	}
};

const mutations = {
	START_OPERATION(state, operationKey) {
		state.operations[operationKey] = true;
	},
	FINISH_OPERATION(state, operationKey) {
		state.operations[operationKey] = false;
	},
	SET_LIST(state, forms) {
		const list = Array.isArray(forms) ? forms : [];
		state.formsList = list;
		for (const form of list) {
			if (form && form.id) {
				Vue.set(state.formsById, form.id, form);
			}
		}
	},
	SET_ASSIGNED_FORMS_FOR_EMPLOYEE(state, { employeeId, forms }) {
		Vue.set(state.assignedFormsByEmployeeId, employeeId, Array.isArray(forms) ? forms : []);
	},
	UPSERT_FORM(state, form) {
		if (!form || !form.id) return;
		Vue.set(state.formsById, form.id, form);
	},
	UPSERT_SUBMISSION(state, submission) {
		if (!submission || (!submission.submissionId && !submission.id)) return;
		const submissionId = submission.id || submission.submissionId;
		Vue.set(state.submissionsById, submissionId, submission);
	}
};

export default {
	namespaced: true,
	state,
	getters,
	actions,
	mutations
};
